type NativeTradeNumbers = Readonly<{
  quantity: string; price: string;
  priceScale: string | null; fractionalQuantityScale: string | null;
  commissionNotionalCollected: string | null;
}>;

function integer(value: string | null, signed = false): bigint | null {
  if (typeof value !== 'string' || !/^(?:0|-?[1-9]\d{0,18})$/.test(value)) return null;
  const n = BigInt(value);
  return n >= (signed ? -(1n << 63n) : 0n) && n < (1n << 63n) ? n : null;
}

function divisor(value: string | null): bigint | null {
  // Published examples use divisors, not exponents. Unknown legacy scales stay raw.
  return typeof value === 'string' && /^10{0,18}$/.test(value) ? BigInt(value) : null;
}

function decimal(value: bigint | null, scale: bigint | null): string | null {
  if (value === null || scale === null) return null;
  const magnitude = value < 0n ? -value : value;
  const whole = magnitude / scale;
  const remainder = magnitude % scale;
  const fraction = remainder.toString().padStart(scale.toString().length - 1, '0').replace(/0+$/, '');
  return `${value < 0n ? '-' : ''}${whole}${remainder ? `.${fraction}` : ''}`;
}

/** Display only: SearchTrades embeds the native Execution message and its Order
 * scales. No trade-state projection, ownership, totals or risk admission here.
 * See docs/261020InstitutionalUsPrivateEvidence.md for the endpoint/type evidence.
 * Saved null commission stays unknown; its original wire presence was not kept. */
export function institutionalTradeValues(leg: NativeTradeNumbers) {
  const priceScale = divisor(leg.priceScale), quantityScale = divisor(leg.fractionalQuantityScale);
  const price = integer(leg.price);
  return {
    contracts: decimal(integer(leg.quantity), quantityScale),
    priceUsd: decimal(price !== null && priceScale !== null && price <= priceScale ? price : null, priceScale),
    commissionUsd: decimal(integer(leg.commissionNotionalCollected, true),
      priceScale !== null && quantityScale !== null ? priceScale * quantityScale : null),
  };
}
